'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Heart, MessageCircle, Share2, Music2, Zap, Image as ImageIcon, Send,
  Copy, ExternalLink, X, ChevronDown, ChevronUp, Rocket, Play, TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { notifyPostLike, notifyPostComment } from '@/lib/notifications';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Post {
  id: string;
  artist_id: string;
  content: string;
  media_url: string | null;
  image_url: string | null;
  post_type: string;
  track_id: string | null;
  shared_track_id: string | null;
  shared_campaign_id: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  user_profiles: { full_name: string; avatar_url: string; genre: string } | null;
  tracks?: { title: string; cover_url: string } | null;
  shared_campaign?: {
    id: string;
    title: string;
    current_price: number;
    momentum_score: number;
    tracks?: { title: string; cover_url: string } | null;
  } | null;
  userLiked?: boolean;
}

interface Poll {
  id: string;
  question: string;
  option_a: string;
  option_b: string;
  votes_a: number;
  votes_b: number;
  is_active: boolean;
  artist_id: string;
  track_id: string | null;
  created_at: string;
  user_profiles: { full_name: string; avatar_url: string } | null;
  userVote?: string | null;
}

interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  parent_id: string | null;
  created_at: string;
  user_profiles: { full_name: string } | null;
  replies?: Comment[];
}

interface Campaign {
  id: string;
  title: string;
  current_price: number;
  momentum_score: number;
  tracks?: { title: string; cover_url: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const gradients = [
  'linear-gradient(135deg, #1B2A4A, #2A9D8F)',
  'linear-gradient(135deg, #A855F7, #EC4899)',
  'linear-gradient(135deg, #2A9D8F, #3BBFB0)',
  'linear-gradient(135deg, #F59E0B, #EF4444)',
  'linear-gradient(135deg, #06B6D4, #A855F7)',
];

function getInitials(name: string) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Share Modal ──────────────────────────────────────────────────────────────
function ShareModal({ postId, title, onClose }: { postId: string; title: string; onClose: () => void }) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://echomint.io';
  const postUrl = `${siteUrl}/pulse?post=${postId}`;
  const text = encodeURIComponent(`Check out this post on EchoMint: ${title}`);
  const encodedUrl = encodeURIComponent(postUrl);

  const shareOptions = [
    { label: 'WhatsApp', color: '#25D366', bg: 'rgba(37,211,102,0.1)', href: `https://wa.me/?text=${text}%20${encodedUrl}` },
    { label: 'X / Twitter', color: '#1DA1F2', bg: 'rgba(29,161,242,0.1)', href: `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}` },
    { label: 'Telegram', color: '#0088CC', bg: 'rgba(0,136,204,0.1)', href: `https://t.me/share/url?url=${encodedUrl}&text=${text}` },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(postUrl).then(() => { toast.success('Link copied!'); onClose(); });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-sm rounded-2xl p-5 space-y-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Share Post</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: 'var(--muted-foreground)' }}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {shareOptions.map((opt) => (
            <a key={opt.label} href={opt.href} target="_blank" rel="noopener noreferrer" onClick={onClose}
              className="flex flex-col items-center gap-2 p-3 rounded-xl transition-all hover:opacity-80"
              style={{ background: opt.bg, border: `1px solid ${opt.color}30` }}>
              <ExternalLink size={18} style={{ color: opt.color }} />
              <span className="text-xs font-semibold" style={{ color: opt.color }}>{opt.label}</span>
            </a>
          ))}
        </div>
        <button onClick={copyLink} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
          <Copy size={15} />Copy Link
        </button>
      </div>
    </div>
  );
}

// ─── Poll Card ────────────────────────────────────────────────────────────────
function PollCard({ poll, onVote }: { poll: Poll; onVote: (pollId: string, choice: 'a' | 'b') => void }) {
  const total = poll.votes_a + poll.votes_b;
  const pctA = total > 0 ? Math.round((poll.votes_a / total) * 100) : 50;
  const pctB = total > 0 ? Math.round((poll.votes_b / total) * 100) : 50;
  const hasVoted = !!poll.userVote;
  const name = poll.user_profiles?.full_name || 'Artist';

  return (
    <div className="glass-card rounded-xl p-4 sm:p-5 border" style={{ borderColor: 'rgba(168,85,247,0.2)' }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: gradients[0] }}>
          {getInitials(name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }} suppressHydrationWarning>{timeAgo(poll.created_at)}</p>
        </div>
        <span className="badge-purple text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 flex-shrink-0">
          <Zap size={10} /> Poll
        </span>
      </div>
      <p className="font-semibold mb-4 text-sm sm:text-base">{poll.question}</p>
      <div className="space-y-2.5">
        {(['a', 'b'] as const).map((choice) => {
          const label = choice === 'a' ? poll.option_a : poll.option_b;
          const pct = choice === 'a' ? pctA : pctB;
          const isMyVote = poll.userVote === choice;
          return (
            <button key={choice} onClick={() => !hasVoted && onVote(poll.id, choice)} disabled={hasVoted}
              className="w-full relative rounded-lg overflow-hidden text-left transition-all"
              style={{ border: `1px solid ${isMyVote ? 'rgba(168,85,247,0.6)' : 'rgba(255,255,255,0.08)'}`, background: 'rgba(255,255,255,0.03)', cursor: hasVoted ? 'default' : 'pointer' }}>
              {hasVoted && <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${pct}%`, background: isMyVote ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.04)' }} />}
              <div className="relative flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium">{label}</span>
                {hasVoted && <span className="text-sm font-bold tabular-nums" style={{ color: isMyVote ? 'var(--primary)' : 'var(--muted-foreground)' }}>{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>{total} vote{total !== 1 ? 's' : ''} · {poll.is_active ? 'Active' : 'Closed'}</p>
    </div>
  );
}

// ─── Comment Item ─────────────────────────────────────────────────────────────
function CommentItem({ comment, depth = 0, onReply }: { comment: Comment; depth?: number; onReply: (parentId: string, content: string) => void }) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplies, setShowReplies] = useState(false);
  const hasReplies = (comment.replies?.length || 0) > 0;

  const handleReply = () => {
    if (!replyText.trim()) return;
    onReply(comment.id, replyText.trim());
    setReplyText('');
    setShowReplyBox(false);
    setShowReplies(true);
  };

  return (
    <div className={depth > 0 ? 'ml-6 mt-2' : ''}>
      <div className="flex gap-2.5">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: gradients[2] }}>
          {getInitials(comment.user_profiles?.full_name || 'U')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="rounded-xl px-3 py-2" style={{ background: 'var(--muted)' }}>
            <p className="text-xs font-semibold mb-0.5">{comment.user_profiles?.full_name || 'User'}</p>
            <p className="text-xs break-words" style={{ color: 'var(--muted-foreground)' }}>{comment.content}</p>
          </div>
          <div className="flex items-center gap-3 mt-1 px-1">
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }} suppressHydrationWarning>{timeAgo(comment.created_at)}</span>
            {depth === 0 && (
              <button onClick={() => setShowReplyBox(!showReplyBox)} className="text-xs font-semibold" style={{ color: 'var(--secondary)' }}>Reply</button>
            )}
            {hasReplies && depth === 0 && (
              <button onClick={() => setShowReplies(!showReplies)} className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {showReplies ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
          {showReplyBox && (
            <div className="flex gap-2 mt-2">
              <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleReply()} placeholder="Write a reply..."
                className="flex-1 px-3 py-1.5 text-xs rounded-lg" style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              <button onClick={handleReply} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--secondary)', color: 'white' }}>
                <Send size={11} />
              </button>
            </div>
          )}
        </div>
      </div>
      {showReplies && hasReplies && (
        <div className="mt-2 space-y-2">
          {comment.replies!.map((reply) => <CommentItem key={reply.id} comment={reply} depth={depth + 1} onReply={onReply} />)}
        </div>
      )}
    </div>
  );
}

// ─── Campaign Share Card (inside post) ───────────────────────────────────────
function CampaignShareCard({ campaign }: { campaign: NonNullable<Post['shared_campaign']> }) {
  return (
    <div className="rounded-xl overflow-hidden mb-4" style={{ background: 'rgba(42,157,143,0.06)', border: '1px solid rgba(42,157,143,0.2)' }}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: gradients[0] }}>
          {campaign.tracks?.cover_url ? (
            <img src={campaign.tracks.cover_url} alt={campaign.tracks.title} className="w-full h-full object-cover" />
          ) : <Music2 size={18} style={{ color: 'var(--secondary)' }} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{campaign.title}</p>
          {campaign.tracks?.title && <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{campaign.tracks.title}</p>}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs font-semibold" style={{ color: 'var(--secondary)' }}>₦{campaign.current_price?.toFixed(2)} / MCT</span>
            {campaign.momentum_score > 0 && (
              <span className="flex items-center gap-1 text-xs" style={{ color: '#C084FC' }}>
                <TrendingUp size={10} />{campaign.momentum_score}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="px-3 pb-3">
        <a href="/campaign-discovery" className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(42,157,143,0.15)', color: 'var(--secondary)', border: '1px solid rgba(42,157,143,0.3)' }}>
          <Rocket size={12} /> View Campaign
        </a>
      </div>
    </div>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────
function PostCard({ post, onLike, onComment }: {
  post: Post;
  onLike: (postId: string, liked: boolean) => void;
  onComment: (postId: string, content: string, parentId?: string) => void;
}) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { user } = useAuth();
  const name = post.user_profiles?.full_name || 'User';
  const imageToShow = post.image_url || post.media_url;

  const loadComments = useCallback(async () => {
    if (!showComments) return;
    setLoadingComments(true);
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('post_comments')
        .select('*, user_profiles(full_name)')
        .eq('post_id', post.id)
        .is('parent_id', null)
        .order('created_at', { ascending: true })
        .limit(30);

      if (data) {
        const withReplies = await Promise.all(
          data.map(async (c: any) => {
            const { data: replies } = await supabase
              .from('post_comments')
              .select('*, user_profiles(full_name)')
              .eq('parent_id', c.id)
              .order('created_at', { ascending: true });
            return { ...c, replies: replies || [] };
          })
        );
        setComments(withReplies);
      }
    } catch {}
    setLoadingComments(false);
  }, [post.id, showComments]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const handleComment = async () => {
    if (!commentText.trim()) return;
    if (!user) { toast.error('Sign in to comment'); return; }
    await onComment(post.id, commentText.trim());
    setCommentText('');
    loadComments();
  };

  const handleReply = async (parentId: string, content: string) => {
    if (!user) { toast.error('Sign in to reply'); return; }
    const supabase = createClient();
    await supabase.from('post_comments').insert({ post_id: post.id, user_id: user.id, content, parent_id: parentId });
    await supabase.rpc('increment_post_comments', { post_uuid: post.id });
    loadComments();
  };

  return (
    <div className="glass-card rounded-xl p-4 sm:p-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden"
          style={{ background: gradients[post.artist_id.charCodeAt(0) % gradients.length] }}>
          {post.user_profiles?.avatar_url ? (
            <img src={post.user_profiles.avatar_url} alt={name} className="w-full h-full object-cover" />
          ) : getInitials(name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }} suppressHydrationWarning>
            {post.user_profiles?.genre || 'Member'} · {timeAgo(post.created_at)}
          </p>
        </div>
        {post.post_type === 'track_share' && (
          <span className="badge-purple text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 flex-shrink-0">
            <Music2 size={10} /> Track
          </span>
        )}
        {post.post_type === 'campaign_share' && (
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 flex-shrink-0"
            style={{ background: 'rgba(42,157,143,0.1)', color: 'var(--secondary)', border: '1px solid rgba(42,157,143,0.2)' }}>
            <Rocket size={10} /> Campaign
          </span>
        )}
      </div>

      {/* Content */}
      {post.content && (
        <p className="text-sm leading-relaxed mb-3 break-words" style={{ color: 'var(--foreground)' }}>{post.content}</p>
      )}

      {/* Image */}
      {imageToShow && (
        <div className="rounded-xl overflow-hidden mb-3" style={{ maxHeight: 320 }}>
          <img src={imageToShow} alt="Post image" className="w-full object-cover" style={{ maxHeight: 320 }} />
        </div>
      )}

      {/* Campaign share card */}
      {post.shared_campaign && <CampaignShareCard campaign={post.shared_campaign} />}

      {/* Track snippet */}
      {post.tracks && !post.shared_campaign && (
        <div className="flex items-center gap-3 p-3 rounded-xl mb-3"
          style={{ background: 'rgba(42,157,143,0.08)', border: '1px solid rgba(42,157,143,0.2)' }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: gradients[1] }}>
            {post.tracks.cover_url ? (
              <img src={post.tracks.cover_url} alt={post.tracks.title} className="w-full h-full object-cover" />
            ) : <Music2 size={16} style={{ color: 'var(--secondary)' }} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{post.tracks.title}</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Track snippet</p>
          </div>
          <Play size={14} style={{ color: 'var(--secondary)', flexShrink: 0 }} />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <button onClick={() => onLike(post.id, post.userLiked || false)}
          className="flex items-center gap-1.5 text-sm transition-all hover:opacity-80"
          style={{ color: post.userLiked ? '#EC4899' : 'var(--muted-foreground)' }}>
          <Heart size={16} fill={post.userLiked ? 'currentColor' : 'none'} />
          <span>{post.likes_count}</span>
        </button>
        <button onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-1.5 text-sm transition-all hover:opacity-80"
          style={{ color: showComments ? 'var(--secondary)' : 'var(--muted-foreground)' }}>
          <MessageCircle size={16} />
          <span>{post.comments_count}</span>
        </button>
        <button onClick={() => setShowShareModal(true)}
          className="flex items-center gap-1.5 text-sm ml-auto transition-all hover:opacity-80"
          style={{ color: 'var(--muted-foreground)' }}>
          <Share2 size={16} />
          <span className="text-xs hidden sm:inline">Share</span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-4 space-y-3">
          {loadingComments ? (
            <div className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>Loading...</div>
          ) : comments.length === 0 ? (
            <p className="text-xs text-center py-2" style={{ color: 'var(--muted-foreground)' }}>No comments yet. Be first!</p>
          ) : (
            <div className="space-y-3">
              {comments.map((c) => <CommentItem key={c.id} comment={c} onReply={handleReply} />)}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleComment()} placeholder="Write a comment..."
              className="flex-1 px-3 py-2 text-xs rounded-lg" style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
            <button onClick={handleComment} className="px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: 'var(--secondary)', color: 'white' }}>
              <Send size={12} />
            </button>
          </div>
        </div>
      )}

      {showShareModal && <ShareModal postId={post.id} title={post.content?.slice(0, 60) || 'Post'} onClose={() => setShowShareModal(false)} />}
    </div>
  );
}

// ─── Create Post Box ──────────────────────────────────────────────────────────
function CreatePostBox({ onPost }: { onPost: () => void }) {
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCampaignPicker, setShowCampaignPicker] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const name = user?.user_metadata?.full_name || 'User';

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG, or WEBP images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const loadCampaigns = async () => {
    if (campaigns.length > 0) { setShowCampaignPicker(true); return; }
    setLoadingCampaigns(true);
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('momentum_campaigns')
        .select('id, title, current_price, momentum_score, tracks(title, cover_url)')
        .eq('is_active', true)
        .order('momentum_score', { ascending: false })
        .limit(20);
      setCampaigns(data || []);
      setShowCampaignPicker(true);
    } catch {}
    setLoadingCampaigns(false);
  };

  const handlePost = async () => {
    if (!content.trim() && !imageFile && !selectedCampaign) return;
    if (!user) { toast.error('Sign in to post'); return; }
    setPosting(true);
    try {
      const supabase = createClient();
      let imageUrl: string | null = null;

      // Upload image if selected
      if (imageFile) {
        const ext = imageFile.name.split('.').pop();
        const path = `pulse/${user.id}/${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('media')
          .upload(path, imageFile, { contentType: imageFile.type, upsert: false });

        if (uploadError) {
          // Fallback: use base64 data URL if storage fails
          imageUrl = imagePreview;
        } else {
          const { data: urlData } = supabase.storage.from('media').getPublicUrl(path);
          imageUrl = urlData?.publicUrl || imagePreview;
        }
      }

      const postType = selectedCampaign ? 'campaign_share' : imageFile ? 'image' : 'text';

      await supabase.from('posts').insert({
        artist_id: user.id,
        content: content.trim(),
        post_type: postType,
        image_url: imageUrl,
        media_url: imageUrl,
        shared_campaign_id: selectedCampaign?.id || null,
        is_published: true,
      });

      setContent('');
      removeImage();
      setSelectedCampaign(null);
      setShowCampaignPicker(false);
      onPost();
      toast.success('Post published!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to post');
    }
    setPosting(false);
  };

  const canPost = (content.trim().length > 0 || imageFile !== null || selectedCampaign !== null) && !posting;

  return (
    <div className="glass-card rounded-xl p-4">
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: gradients[0] }}>
          {getInitials(name)}
        </div>
        <div className="flex-1 min-w-0">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share an update, thought, or track with the community..."
            rows={3}
            className="w-full px-3 py-2 text-sm rounded-xl resize-none"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          />

          {/* Image preview */}
          {imagePreview && (
            <div className="relative mt-2 rounded-xl overflow-hidden" style={{ maxHeight: 200 }}>
              <img src={imagePreview} alt="Preview" className="w-full object-cover" style={{ maxHeight: 200 }} />
              <button onClick={removeImage} className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}>
                <X size={12} />
              </button>
            </div>
          )}

          {/* Campaign picker */}
          {showCampaignPicker && (
            <div className="mt-2 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--card)', maxHeight: 200, overflowY: 'auto' }}>
              <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold">Select Campaign to Share</p>
                <button onClick={() => setShowCampaignPicker(false)} style={{ color: 'var(--muted-foreground)' }}><X size={14} /></button>
              </div>
              {campaigns.length === 0 ? (
                <p className="text-xs text-center py-4" style={{ color: 'var(--muted-foreground)' }}>No active campaigns found</p>
              ) : (
                campaigns.map((c) => (
                  <button key={c.id} onClick={() => { setSelectedCampaign(c); setShowCampaignPicker(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors">
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: gradients[0] }}>
                      {c.tracks?.cover_url && <img src={c.tracks.cover_url} alt={c.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{c.title}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>₦{c.current_price?.toFixed(2)} / MCT</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Selected campaign preview */}
          {selectedCampaign && !showCampaignPicker && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(42,157,143,0.08)', border: '1px solid rgba(42,157,143,0.2)' }}>
              <Rocket size={13} style={{ color: 'var(--secondary)', flexShrink: 0 }} />
              <p className="text-xs font-semibold flex-1 truncate">{selectedCampaign.title}</p>
              <button onClick={() => setSelectedCampaign(null)} style={{ color: 'var(--muted-foreground)' }}><X size={13} /></button>
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageSelect} />
              <button onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                style={{ color: imageFile ? 'var(--secondary)' : 'var(--muted-foreground)' }}
                title="Add image">
                <ImageIcon size={15} />
              </button>
              <button onClick={loadCampaigns} disabled={loadingCampaigns}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                style={{ color: selectedCampaign ? 'var(--secondary)' : 'var(--muted-foreground)' }}
                title="Share a campaign">
                <Rocket size={15} />
              </button>
              <button className="p-1.5 rounded-lg hover:bg-muted transition-colors" style={{ color: 'var(--muted-foreground)' }} title="Share a track">
                <Music2 size={15} />
              </button>
            </div>
            <button onClick={handlePost} disabled={!canPost}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
              style={{ background: 'var(--secondary)', color: 'white' }}>
              {posting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main PulseFeed ───────────────────────────────────────────────────────────
export default function PulseFeed() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedType, setFeedType] = useState<'all' | 'posts' | 'polls'>('all');
  const { user } = useAuth();

  const fetchFeed = useCallback(async () => {
    try {
      const supabase = createClient();
      const [postsRes, pollsRes] = await Promise.all([
        supabase
          .from('posts')
          .select('*, user_profiles(full_name, avatar_url, genre), tracks(title, cover_url)')
          .eq('is_published', true)
          .order('created_at', { ascending: false })
          .limit(30),
        supabase
          .from('polls')
          .select('*, user_profiles(full_name, avatar_url)')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      let postsData: Post[] = postsRes.data || [];
      let pollsData: Poll[] = pollsRes.data || [];

      // Load shared campaign data for campaign_share posts
      const campaignIds = postsData
        .filter(p => p.shared_campaign_id)
        .map(p => p.shared_campaign_id as string);

      let campaignMap: Record<string, Campaign> = {};
      if (campaignIds.length > 0) {
        const { data: campaignData } = await supabase
          .from('momentum_campaigns')
          .select('id, title, current_price, momentum_score, tracks(title, cover_url)')
          .in('id', campaignIds);
        if (campaignData) {
          campaignData.forEach((c: any) => { campaignMap[c.id] = c; });
        }
      }

      postsData = postsData.map(p => ({
        ...p,
        shared_campaign: p.shared_campaign_id ? campaignMap[p.shared_campaign_id] || null : null,
      }));

      if (user) {
        const postIds = postsData.map(p => p.id);
        const pollIds = pollsData.map(p => p.id);

        const [likesRes, votesRes] = await Promise.all([
          postIds.length > 0
            ? supabase.from('post_likes').select('post_id').eq('user_id', user.id).in('post_id', postIds)
            : Promise.resolve({ data: [] }),
          pollIds.length > 0
            ? supabase.from('poll_votes').select('poll_id, choice').eq('user_id', user.id).in('poll_id', pollIds)
            : Promise.resolve({ data: [] }),
        ]);

        const likedSet = new Set((likesRes.data || []).map((l: any) => l.post_id));
        const voteMap = new Map((votesRes.data || []).map((v: any) => [v.poll_id, v.choice]));

        postsData = postsData.map(p => ({ ...p, userLiked: likedSet.has(p.id) }));
        pollsData = pollsData.map(p => ({ ...p, userVote: voteMap.get(p.id) || null }));
      }

      setPosts(postsData);
      setPolls(pollsData);
    } catch {}
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const handleLike = async (postId: string, liked: boolean) => {
    if (!user) { toast.error('Sign in to like posts'); return; }
    const supabase = createClient();

    // Optimistic update
    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, userLiked: !liked, likes_count: Math.max(0, p.likes_count + (liked ? -1 : 1)) }
      : p
    ));

    try {
      if (liked) {
        await supabase.from('post_likes').delete().eq('post_id', postId).eq('user_id', user.id);
        const { error } = await supabase.rpc('decrement_post_likes', { post_uuid: postId });
        if (error) {
          const { count } = await supabase.from('post_likes').select('*', { count: 'exact', head: true }).eq('post_id', postId);
          if (count !== null) await supabase.from('posts').update({ likes_count: count }).eq('id', postId);
        }
      } else {
        const { error: insertError } = await supabase.from('post_likes').insert({ post_id: postId, user_id: user.id });
        if (insertError && insertError.code === '23505') {
          setPosts(prev => prev.map(p => p.id === postId ? { ...p, userLiked: true, likes_count: p.likes_count - 1 } : p));
          return;
        }
        const { error } = await supabase.rpc('increment_post_likes', { post_uuid: postId });
        if (error) {
          const { count } = await supabase.from('post_likes').select('*', { count: 'exact', head: true }).eq('post_id', postId);
          if (count !== null) {
            await supabase.from('posts').update({ likes_count: count }).eq('id', postId);
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: count } : p));
          }
        }

        // Notify post owner
        const post = posts.find(p => p.id === postId);
        if (post && post.artist_id !== user.id) {
          const likerName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Someone';
          await notifyPostLike({
            postOwnerId: post.artist_id,
            likerId: user.id,
            likerName,
            postId,
            postPreview: post.content || 'a post',
          });
        }
      }
    } catch {
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, userLiked: liked, likes_count: Math.max(0, p.likes_count + (liked ? 1 : -1)) }
        : p
      ));
    }
  };

  const handleComment = async (postId: string, content: string) => {
    if (!user) { toast.error('Sign in to comment'); return; }
    const supabase = createClient();
    const { error } = await supabase.from('post_comments').insert({ post_id: postId, user_id: user.id, content, parent_id: null });
    if (error) { toast.error('Failed to post comment'); return; }

    const { error: rpcError } = await supabase.rpc('increment_post_comments', { post_uuid: postId });
    if (rpcError) {
      const { count } = await supabase.from('post_comments').select('*', { count: 'exact', head: true }).eq('post_id', postId).is('parent_id', null);
      if (count !== null) await supabase.from('posts').update({ comments_count: count }).eq('id', postId);
    }

    setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p));

    // Notify post owner
    const post = posts.find(p => p.id === postId);
    if (post && post.artist_id !== user.id) {
      const commenterName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Someone';
      await notifyPostComment({
        postOwnerId: post.artist_id,
        commenterId: user.id,
        commenterName,
        postId,
        commentContent: content,
      });
    }
  };

  const handleVote = async (pollId: string, choice: 'a' | 'b') => {
    if (!user) { toast.error('Sign in to vote'); return; }
    const supabase = createClient();
    await supabase.rpc('cast_poll_vote', { poll_uuid: pollId, user_uuid: user.id, vote_choice: choice });
    setPolls(prev => prev.map(p => p.id === pollId
      ? { ...p, userVote: choice, votes_a: choice === 'a' ? p.votes_a + 1 : p.votes_a, votes_b: choice === 'b' ? p.votes_b + 1 : p.votes_b }
      : p
    ));
  };

  // Interleave posts and polls
  const feedItems: { type: 'post' | 'poll'; data: Post | Poll }[] = [];
  let pi = 0, qi = 0;
  while (pi < posts.length || qi < polls.length) {
    if (pi < posts.length) feedItems.push({ type: 'post', data: posts[pi++] });
    if (pi < posts.length) feedItems.push({ type: 'post', data: posts[pi++] });
    if (qi < polls.length) feedItems.push({ type: 'poll', data: polls[qi++] });
  }

  const filteredItems = feedType === 'posts'
    ? feedItems.filter(i => i.type === 'post')
    : feedType === 'polls'
    ? feedItems.filter(i => i.type === 'poll')
    : feedItems;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Zap size={20} style={{ color: 'var(--secondary)' }} />
          <h2 className="text-xl font-bold">PULSE Feed</h2>
          <span className="badge-purple text-xs px-2 py-0.5 rounded-full font-semibold">LIVE</span>
        </div>
        <div className="flex items-center gap-1 rounded-xl p-1" style={{ background: 'var(--muted)' }}>
          {(['all', 'posts', 'polls'] as const).map((t) => (
            <button key={t} onClick={() => setFeedType(t)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all"
              style={{ background: feedType === t ? 'var(--card)' : 'transparent', color: feedType === t ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Create post — available to ALL signed-in users */}
      {user && <CreatePostBox onPost={fetchFeed} />}

      {/* Feed */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton rounded-xl h-40" />)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="glass-card rounded-xl p-10 sm:p-12 text-center">
          <Zap size={36} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
          <p className="font-semibold mb-1">PULSE feed is quiet</p>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {user ? 'Be the first to post an update!' : 'Sign in to see and post updates.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item, idx) =>
            item.type === 'post' ? (
              <PostCard key={`post-${(item.data as Post).id}`} post={item.data as Post} onLike={handleLike} onComment={handleComment} />
            ) : (
              <PollCard key={`poll-${(item.data as Poll).id}`} poll={item.data as Poll} onVote={handleVote} />
            )
          )}
        </div>
      )}
    </div>
  );
}
